import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Play, Shield, Check, ChevronDown, VolumeX, AlertTriangle, X } from "lucide-react";

const VSL_VIDEO_URL = "https://www.youtube.com/watch?v=5AXwVv_vIaY";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_elite-scout-mvp/artifacts/uw8m7f8v_Simple%20Sports%20Football%20%26%20Soccer%20Badge%20Logo.png";

function parseVideoUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { kind: "youtube", id: yt[1] };
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return { kind: "vimeo", id: vm[1] };
  return { kind: "file", fileSrc: url };
}

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setVisible(true), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(18px)",
      transition: `opacity 600ms ease ${delay}ms, transform 600ms ease ${delay}ms`,
    }}>{children}</div>
  );
}

function SmartVideo() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const v = parseVideoUrl(VSL_VIDEO_URL);
  const isEmbed = v && (v.kind === "youtube" || v.kind === "vimeo");
  const ytSrc = v && v.kind === "youtube"
    ? `https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&mute=${muted ? 1 : 0}&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&showinfo=0&controls=1&fs=1&color=white`
    : null;
  const vmSrc = v && v.kind === "vimeo"
    ? `https://player.vimeo.com/video/${v.id}?autoplay=1&muted=${muted ? 1 : 0}&title=0&byline=0&portrait=0`
    : null;

  return (
    <div ref={ref} className="relative aspect-video w-full rounded-xl overflow-hidden bg-[#0e0e0e] border border-[#1f1f1f] shadow-[0_40px_80px_-40px_rgba(212,175,55,0.25)]" data-testid="vsl-video-frame">
      {!v && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#141414] to-[#0A0A0A]" data-testid="vsl-video-placeholder">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mb-4 shadow-[0_0_60px_rgba(212,175,55,0.4)]">
            <Play size={32} className="text-[#0A0A0A] ml-1" fill="#0A0A0A" />
          </div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#777]">Video coming soon</p>
        </div>
      )}
      {v && v.kind === "youtube" && (
        inView ? (
          <iframe key={muted ? "m" : "u"} className="w-full h-full" title="Elite Scouting Network" src={ytSrc}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen data-testid="vsl-video" />
        ) : (
          <div className="absolute inset-0 bg-[#0e0e0e] flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 animate-pulse" />
          </div>
        )
      )}
      {v && v.kind === "vimeo" && inView && (
        <iframe key={muted ? "m" : "u"} className="w-full h-full" title="Elite Scouting Network" src={vmSrc}
          allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="vsl-video" />
      )}
      {v && v.kind === "file" && (
        <video className="w-full h-full object-cover" src={v.fileSrc} controls playsInline autoPlay muted data-testid="vsl-video" />
      )}
      <div className="absolute top-0 left-0 w-10 h-[2px] bg-[#D4AF37] pointer-events-none" />
      <div className="absolute top-0 left-0 w-[2px] h-10 bg-[#D4AF37] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-10 h-[2px] bg-[#D4AF37] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[2px] h-10 bg-[#D4AF37] pointer-events-none" />
      {isEmbed && inView && muted && (
        <button type="button" onClick={() => setMuted(false)}
          className="absolute bottom-4 right-4 z-20 flex items-center gap-2.5 bg-[#D4AF37] text-[#0A0A0A] px-4 py-2.5 rounded-full shadow-[0_10px_30px_-6px_rgba(212,175,55,0.6),0_0_0_4px_rgba(10,10,10,0.8)] hover:bg-[#E5C04A] active:scale-95 transition-all font-black tracking-widest text-[11px] uppercase animate-[vslPulse_1.6s_ease-in-out_infinite]"
          data-testid="vsl-unmute-btn" aria-label="Tap for sound">
          <VolumeX size={15} /><span>Tap for sound</span>
        </button>
      )}
      <style>{`@keyframes vslPulse { 0%,100% { box-shadow: 0 10px 30px -6px rgba(212,175,55,0.5), 0 0 0 4px rgba(10,10,10,0.8), 0 0 0 0 rgba(212,175,55,0.6); } 50% { box-shadow: 0 10px 30px -6px rgba(212,175,55,0.6), 0 0 0 4px rgba(10,10,10,0.8), 0 0 0 14px rgba(212,175,55,0); } }`}</style>
    </div>
  );
}

function PrimaryCTA({ testId = "vsl-cta", subLabel, label = "Get Inside The Network", size = "lg" }) {
  const sizing = size === "md" ? "px-8 py-4 text-xs sm:text-sm" : "px-10 py-5 text-sm sm:text-base";
  return (
    <div className="flex flex-col items-center">
      <Link to="/start"
        className={`group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0A0A0A] font-black tracking-widest uppercase ${sizing} rounded-full hover:bg-[#E5C04A] active:scale-[0.98] transition-all shadow-[0_0_0_1px_rgba(212,175,55,0.4),0_20px_40px_-20px_rgba(212,175,55,0.6)] hover:shadow-[0_0_0_1px_rgba(212,175,55,0.6),0_30px_60px_-20px_rgba(212,175,55,0.8)]`}
        data-testid={testId}>
        {label}
        <ArrowRight size={size === "md" ? 16 : 18} className="group-hover:translate-x-1 transition-transform" />
      </Link>
      {subLabel && <p className="text-[11px] text-[#888] mt-4 tracking-wide">{subLabel}</p>}
    </div>
  );
}

/* ──────────────────────────── SECTIONS ──────────────────────────── */

const TESTIMONIALS = [
  { q: "First scout opened my profile within 9 days. Trial booked.", n: "Dmitri", c: "Romania" },
  { q: "Finally something that makes me look properly professional.", n: "Kwame", c: "Ghana" },
  { q: "Got contacted by a club in Belgium six weeks after going live.", n: "Amadou", c: "Senegal" },
];

function TestimonialStrip() {
  return (
    <section className="relative py-10 sm:py-14 border-y border-[#141414]" data-testid="vsl-testimonials">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-5 h-full">
                <div className="flex gap-0.5 mb-3" aria-label="5 star rating">
                  {[0,1,2,3,4].map(s => (
                    <svg key={s} width="11" height="11" viewBox="0 0 20 20" fill="#D4AF37" aria-hidden="true">
                      <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L10 14.9 4.8 17.7l1-5.8L1.5 7.7l5.9-.9z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-[13px] sm:text-[14px] text-[#e5e5e5] leading-[1.6] italic mb-3">"{t.q}"</p>
                <p className="text-[11px] text-[#888] tracking-wide">— {t.n}, <span className="text-[#D4AF37]/80">{t.c}</span></p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function InvisibleWindow() {
  return (
    <section className="relative py-20 sm:py-28 bg-[#0c0c0c]" data-testid="vsl-window">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[420px] rounded-full bg-[#D4AF37]/[0.06] blur-[130px]" />
      </div>
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <p className="text-[10px] sm:text-[11px] text-[#D4AF37] tracking-[0.35em] uppercase mb-4">The Hidden Threat</p>
          <h2 className="text-4xl sm:text-6xl font-black tracking-[-0.02em] leading-[1.0] text-[#F5F5F5]" data-testid="vsl-window-headline">
            The <span className="text-[#D4AF37]">Invisible Window</span>.
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p className="text-lg sm:text-2xl font-medium leading-[1.5] text-[#c8c8c8] mt-8 max-w-2xl mx-auto">
            The five years between 18 and 23 where every door in football opens — and then closes. Forever.
          </p>
          <p className="text-base sm:text-lg text-[#9a9a9a] mt-6 max-w-2xl mx-auto leading-relaxed">
            You don't get a warning when it closes. You feel completely fine. Until one day, you're 24, your friends are signing contracts, and no one's taking your calls.
          </p>
        </Reveal>
        <Reveal delay={220}>
          <div className="grid grid-cols-2 gap-4 mt-12 max-w-xl mx-auto">
            <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-5 text-left">
              <p className="text-3xl sm:text-4xl font-black text-[#D4AF37]">1 in 1,000</p>
              <p className="text-[12px] text-[#9a9a9a] mt-2 leading-snug">players globally ever play a single professional minute</p>
            </div>
            <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-5 text-left">
              <p className="text-3xl sm:text-4xl font-black text-[#D4AF37]">−80%</p>
              <p className="text-[12px] text-[#9a9a9a] mt-2 leading-snug">drop in your odds of breaking through after age 23</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const FAILS = [
  { t: "Highlight reels", b: "Scouts close them in 10 seconds. They don't have time." },
  { t: "Cold DMs", b: "Scouts get 50 a day. Yours sits in a folder. Unopened." },
  { t: "Instagram followers", b: "Clubs don't recruit from likes. 200k followers gets you zero contracts." },
];

function NotWorking() {
  return (
    <section className="relative py-20 sm:py-28" data-testid="vsl-not-working">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14">
          <Reveal>
            <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-4">What no one tells you</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-[-0.02em] leading-[1.05] text-[#F5F5F5]">
              The things you think are getting you noticed —
              <span className="block text-[#D4AF37] mt-2">aren't.</span>
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FAILS.map((f, i) => (
            <Reveal key={f.t} delay={i * 100}>
              <div className="bg-[#0E0E0E] border border-[#1A1A1A] p-6 h-full relative overflow-hidden">
                <X size={22} className="text-[#7A2A2A] mb-4" strokeWidth={2.5} />
                <h3 className="text-lg font-bold text-[#F5F5F5] mb-2">{f.t}</h3>
                <p className="text-[13px] text-[#9a9a9a] leading-[1.7]">{f.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={400}>
          <p className="text-center text-[14px] sm:text-[16px] text-[#cfcfcf] max-w-xl mx-auto mt-12 italic leading-relaxed">
            You could be doing all of it perfectly — and still be completely invisible to the people who decide your future.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Mechanism() {
  return (
    <section className="relative py-20 sm:py-28 bg-[#0c0c0c]" data-testid="vsl-mechanism">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-4">What clubs actually want</p>
          <h2 className="text-3xl sm:text-5xl font-black tracking-[-0.02em] leading-[1.05] text-[#F5F5F5]">
            One structured profile. <span className="text-[#D4AF37]">30 seconds.</span><br />
            In front of the people who decide.
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="text-base sm:text-lg text-[#9a9a9a] mt-8 leading-relaxed">
            Clubs are looking. Right now. In every country. They've changed how they find players in the last three years — and almost nobody has caught up.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ProfilePreview() {
  return (
    <section className="relative py-20 sm:py-28" data-testid="vsl-profile-preview">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-12">
          <Reveal>
            <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-4">This is what yours will look like</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.1] text-[#F5F5F5]">
              A profile clubs actually open.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={120}>
          <div className="relative bg-[#0E0E0E] border border-[#1A1A1A] p-6 sm:p-10 rounded-md shadow-[0_40px_80px_-40px_rgba(212,175,55,0.15)]">
            <div className="flex items-center justify-between mb-5">
              <span className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-[0.22em]">Live Profile</span>
              <span className="text-[9px] font-bold text-[#D4AF37]/80">Profile Strength 92%</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-6 sm:gap-8 items-start">
              <div className="order-2 sm:order-1 min-w-0">
                <div className="inline-block text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] px-2.5 py-1 rounded-sm tracking-wider uppercase mb-3">Centre-Forward</div>
                <h3 className="text-2xl sm:text-3xl font-black tracking-[-0.02em] uppercase text-[#F5F5F5] leading-tight mb-2">Marcus Bailey</h3>
                <p className="text-[13px] sm:text-[14px] text-[#D4AF37] leading-snug mb-5">Direct, aerial-strong No. 9 with composure in the box.</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 mb-5 pb-5 border-b border-[#1A1A1A]">
                  {[{l:"Age",v:"21"},{l:"Foot",v:"Right"},{l:"Based In",v:"England"},{l:"Club",v:"Semi-Pro"}].map((s,i)=>(
                    <div key={i} className="flex flex-col">
                      <span className="text-[9px] font-bold text-[#666] uppercase tracking-[0.12em]">{s.l}</span>
                      <span className="text-[12px] text-[#eee] font-medium">{s.v}</span>
                    </div>
                  ))}
                </div>
                <span className="text-[9px] font-bold text-[#666] uppercase tracking-[0.15em] block mb-2">Key Attributes</span>
                <div className="flex flex-wrap gap-1.5">
                  {["Aerial Ability · Elite","Finishing · High","Hold-up Play · High","Off-Ball Movement · High"].map((a)=>(
                    <span key={a} className="text-[10px] bg-[#141414] text-[#ccc] border border-[#1A1A1A] px-2 py-1 rounded-sm">{a}</span>
                  ))}
                </div>
              </div>
              <div className="order-1 sm:order-2">
                <div className="aspect-[3/4] overflow-hidden border border-[#1A1A1A] bg-[#0A0A0A]">
                  <div className="w-full h-full bg-gradient-to-b from-[#141414] to-[#0A0A0A] flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-14 h-14 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-2">
                        <span className="text-[#D4AF37] text-xl font-black">MB</span>
                      </div>
                      <p className="text-[9px] text-[#666] tracking-wider uppercase">Profile Photo</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function NetworkProof() {
  return (
    <section className="relative py-20 sm:py-28 bg-[#0c0c0c]" data-testid="vsl-network-proof">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-4">Inside the network</p>
          <p className="text-2xl sm:text-3xl md:text-4xl font-semibold leading-[1.35] text-[#e8e8e8]">
            Players from Nigeria, Ghana, Romania, Brazil and Vietnam — already inside.
          </p>
          <p className="text-lg sm:text-xl text-[#D4AF37] mt-6 leading-snug">
            Profiles opened by scouts across Europe.<br className="hidden sm:block" />
            Trials secured. Real movement.
          </p>
          <p className="text-[11px] text-[#666] mt-10 tracking-[0.2em] uppercase">
            14 countries · Growing weekly
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function WorstCase() {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="vsl-worst-case">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full bg-[#3a1010]/30 blur-[140px]" />
      </div>
      <div className="relative max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <AlertTriangle size={28} className="text-[#D4AF37] mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl sm:text-5xl font-black tracking-[-0.02em] leading-[1.05] text-[#F5F5F5]">
            Now picture this.
          </h2>
        </Reveal>
        <Reveal delay={150}>
          <p className="text-base sm:text-xl text-[#cfcfcf] mt-8 leading-[1.7]">
            You're 27. Sat in an office. Scrolling Instagram on your lunch break.
          </p>
          <p className="text-base sm:text-xl text-[#cfcfcf] mt-4 leading-[1.7]">
            A 19-year-old you used to play against just signed for a club abroad.
          </p>
          <p className="text-lg sm:text-2xl text-[#D4AF37] mt-8 font-semibold leading-snug">
            And you know — quietly — that you were better than him at 18.<br />
            But nobody ever saw you.
          </p>
          <p className="text-base sm:text-lg text-[#9a9a9a] mt-8 leading-relaxed italic">
            That's the version of yourself we make sure doesn't exist.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FilterAndGuarantee() {
  return (
    <section className="relative py-20 sm:py-24 bg-[#0c0c0c]" data-testid="vsl-filter">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-8">
          <Reveal>
            <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-4">The cost of getting in</p>
            <p className="text-6xl sm:text-7xl font-black text-[#F5F5F5] tracking-tight leading-none">
              $19<span className="text-2xl sm:text-3xl font-normal text-[#888] align-middle"> one-off</span>
            </p>
            <p className="text-[12px] sm:text-sm text-[#888] mt-3 tracking-wide">
              No subscription. No monthly charges. Less than a takeaway.
            </p>
          </Reveal>
        </div>

        {/* The Filter */}
        <Reveal delay={140}>
          <div className="border-l-2 border-[#D4AF37] bg-[#0E0E0E] p-6 sm:p-8 my-10" data-testid="vsl-filter-block">
            <p className="text-[15px] sm:text-[16px] text-[#E5E5E5] leading-[1.75] font-medium">
              If $19 is too much to give your own career a real chance — then you've already given up. You just haven't admitted it yet.
            </p>
            <p className="text-[13px] sm:text-sm text-[#9a9a9a] mt-3 leading-relaxed">
              This isn't for you. We don't want money from players who don't believe in this.
            </p>
          </div>
        </Reveal>

        {/* Guarantee */}
        <Reveal delay={240}>
          <div className="bg-[#0E0E0E] border border-[#D4AF37]/20 p-6 sm:p-7 text-center" data-testid="vsl-guarantee">
            <Shield className="text-[#D4AF37] mx-auto mb-3" size={26} />
            <h3 className="text-lg sm:text-xl font-semibold text-[#F5F5F5] mb-2">14-Day No-Questions Refund</h3>
            <p className="text-[13px] sm:text-sm text-[#9a9a9a] leading-[1.65] max-w-md mx-auto">
              For everyone serious — if it's not for you, email us within 14 days. Full refund. No forms. No questions.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const FAQS = [
  { q: "I'm not from the UK or Europe. Does this work for me?", a: "Yes. The network is global by design. Players from West Africa, Eastern Europe, Asia and the Americas are already inside. Scouts review profiles internationally — that's the whole point of being structured properly." },
  { q: "Do I need a highlight video to get started?", a: "No. You can add one later. Many players come in without one and add it once they're inside the network." },
  { q: "What happens if no club opens my profile?", a: "Your profile stays live inside the network, you get a shareable link you can send to any coach, scout or agent yourself, and you've got 14 days to ask for a full refund — no questions." },
  { q: "Is this a subscription? Will I get charged again?", a: "No. $19 is a one-off payment. No auto-renewal. No monthly charges. Pay once, that's it." },
  { q: "How long does it take to get my profile live?", a: "Under 10 minutes. You answer simple questions, the platform builds your profile around how scouts actually assess players, and you're inside." },
];

function FAQItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1A1A1A]">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 py-5 sm:py-6 text-left hover:text-[#D4AF37] transition-colors" data-testid={`faq-toggle-${i}`}>
        <span className="text-[15px] sm:text-[16px] font-semibold text-[#F5F5F5] pr-4">{q}</span>
        <ChevronDown size={18} className={`text-[#888] flex-shrink-0 transition-transform ${open ? "rotate-180 text-[#D4AF37]" : ""}`} />
      </button>
      <div className="overflow-hidden transition-all" style={{ maxHeight: open ? "400px" : "0", opacity: open ? 1 : 0 }}>
        <p className="text-[14px] text-[#9a9a9a] leading-[1.7] pb-6 pr-8" data-testid={`faq-answer-${i}`}>{a}</p>
      </div>
    </div>
  );
}

function FAQSection() {
  return (
    <section className="relative py-20 sm:py-24" data-testid="vsl-faq">
      <div className="max-w-2xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10">
          <Reveal>
            <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-3">Before you decide</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-[-0.02em] leading-[1.1] text-[#F5F5F5]">
              Common questions.
            </h2>
          </Reveal>
        </div>
        <div>{FAQS.map((f, i) => <FAQItem key={i} i={i} q={f.q} a={f.a} />)}</div>
      </div>
    </section>
  );
}

function MobileStickyCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className={`md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-[#D4AF37]/30 px-4 py-3 transition-transform duration-300 ${visible ? "translate-y-0" : "translate-y-full"}`} data-testid="vsl-mobile-sticky">
      <Link to="/start" className="flex items-center justify-between gap-3 bg-[#D4AF37] text-[#0A0A0A] font-black tracking-wider uppercase text-[13px] px-5 py-3.5 rounded-full active:scale-[0.98] transition-all shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)]" data-testid="vsl-sticky-cta">
        <span>$19 one-off — Get inside</span>
        <ArrowRight size={16} />
      </Link>
    </div>
  );
}

export default function VSL() {
  return (
    <div className="bg-[#0A0A0A] text-[#F5F5F5] overflow-hidden pb-20 md:pb-0" data-testid="vsl-page"
      style={{ fontFamily: "'Cabinet Grotesk', 'Inter', sans-serif" }}>
      {/* Minimal brand bar */}
      <div className="w-full border-b border-[#141414]" data-testid="vsl-brand-bar">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-2.5">
          <img src={LOGO_URL} alt="" className="h-6 w-6 rounded-full opacity-80" />
          <span className="text-[10px] text-[#777] tracking-[0.3em] uppercase">Elite Scouting Network</span>
        </div>
      </div>

      {/* HERO */}
      <section className="relative" data-testid="vsl-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[560px] h-[380px] rounded-full bg-[#D4AF37]/[0.07] blur-[120px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-6 text-center">
          <Reveal>
            <p className="text-[10px] sm:text-[11px] text-[#D4AF37] tracking-[0.35em] uppercase mb-5" data-testid="vsl-hero-eyebrow">
              If you play football and you're 16–24 — watch this.
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05] mb-5" data-testid="vsl-hero-headline">
              The boys you used to be<br />better than are signing<br /><span className="text-[#D4AF37]">pro contracts.</span>
            </h1>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-base sm:text-lg text-[#cfcfcf] max-w-2xl mx-auto leading-relaxed" data-testid="vsl-hero-sub">
              Here's what they did differently — and how you do the same.
            </p>
          </Reveal>
        </div>

        {/* Video */}
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pb-8 sm:pb-12" data-testid="vsl-video-section">
          <p className="text-center text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-3">
            Watch — 3 Min — From inside football
          </p>
          <SmartVideo />

          <p className="text-center text-[12px] sm:text-[13px] text-[#9a9a9a] mt-6 leading-relaxed" data-testid="vsl-price-condition">
            <span className="text-[#D4AF37] font-semibold">$19 · one-off</span>. No subscription.<br className="hidden sm:block" />
            <span className="text-[#6a6a6a]">14-day refund. For players who are serious.</span>
          </p>

          <p className="text-center text-[11px] sm:text-[12px] text-[#D4AF37]/90 mt-3 tracking-wide font-medium" data-testid="vsl-scarcity">
            ⚡ Founding member pricing — locked while we're under 1,000 players.
          </p>

          <Reveal delay={100}>
            <div className="mt-10 flex flex-col items-center" data-testid="vsl-cta-after-video">
              <PrimaryCTA testId="vsl-cta-after-video-btn" size="md" label="Build My Profile — $19" subLabel="Takes under 10 minutes" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonials — social proof above the fold-2 */}
      <TestimonialStrip />

      {/* 1 · The Invisible Window */}
      <InvisibleWindow />

      {/* 2 · What's NOT working (disqualification) */}
      <NotWorking />

      {/* 3 · What clubs actually want */}
      <Mechanism />

      {/* 4 · Profile preview */}
      <ProfilePreview />

      {/* 5 · Network proof */}
      <NetworkProof />

      {/* 6 · Worst-case visualization */}
      <WorstCase />

      {/* 7 · Filter + guarantee */}
      <FilterAndGuarantee />

      {/* 8 · FAQ */}
      <FAQSection />

      {/* FINAL CTA */}
      <section className="relative py-24 sm:py-32" data-testid="vsl-final-cta">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/[0.10] blur-[140px]" />
        </div>
        <div className="relative max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-[10px] text-[#D4AF37] tracking-[0.35em] uppercase mb-5">The window is open right now</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-[-0.02em] leading-[1.05] text-[#F5F5F5] mb-4">
              In five years, you're either<br />playing professional football…
            </h2>
            <p className="text-3xl sm:text-5xl font-black tracking-[-0.02em] leading-[1.05] text-[#D4AF37] mb-10">
              …or telling your kids you<br />could've been.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="inline-block mb-8">
              <p className="text-4xl sm:text-5xl font-black tracking-tight text-[#F5F5F5]">
                $19<span className="text-xl sm:text-2xl font-normal text-[#888]"> one-off</span>
              </p>
              <p className="text-[11px] text-[#A3A3A3] tracking-wide mt-2">
                No subscription · 14-day refund · For players who are serious
              </p>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <PrimaryCTA testId="vsl-final-cta-button" label="Take The Step — $19" subLabel="That choice is being made today" />
          </Reveal>
          <Reveal delay={240}>
            <p className="text-[11px] sm:text-[12px] text-[#D4AF37]/80 mt-5 tracking-wide font-medium">
              ⚡ Founding pricing — locked while we're under 1,000 players.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <div className="flex items-center justify-center gap-6 sm:gap-8 mt-10 flex-wrap text-[11px] text-[#666]">
              <span className="flex items-center gap-1.5"><Check size={12} className="text-[#D4AF37]" /> 14-day refund</span>
              <span className="flex items-center gap-1.5"><Check size={12} className="text-[#D4AF37]" /> Secure via Stripe</span>
              <span className="flex items-center gap-1.5"><Check size={12} className="text-[#D4AF37]" /> One-off, no subscription</span>
            </div>
          </Reveal>
        </div>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 mt-16">
          <div className="border-t border-[#141414] pt-6 flex items-center justify-center gap-2.5">
            <img src={LOGO_URL} alt="" className="h-5 w-5 rounded-full opacity-60" />
            <span className="text-[10px] text-[#555] tracking-[0.3em] uppercase">Elite Scouting Network · We move players</span>
          </div>
        </div>
      </section>

      <MobileStickyCTA />
    </div>
  );
}
